import { useQuery } from '@apollo/client';
import { Button } from '@mui/material';
import { Formik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { BaseDialog } from '../../../../base/components/BaseDialog';
import { Flex } from '../../../../base/components/Flex';
import FormikForm from '../../../../base/components/form/FormikForm';
import FormikTextField from '../../../../base/components/form/FormikTextField';
import { Loader } from '../../../../client/components/Loader';
import {
  CompanyQrVideosDocument,
  UpdateCompanyQrVideosDocument,
} from '../../../../graphql/graphql-operations';
import { useMutationWithSnackbar } from '../../../../hooks/useMutationWithSnackbar';
import {
  TFunctionPrefixed,
  useTranslationPrefix,
} from '../../../../hooks/useTranslationPrefix';

interface FormData {
  primaryHeadsetWakeup: string;
  backupHeadsetWakeup: string;
  launchInstruction: string;
  primaryHeadsetInstruction: string;
  backupHeadsetInstruction: string;
  returnHeadsetInstruction: string;
}

interface Props {
  companyId: string;
  open: boolean;
  onClose: () => void;
}

const EditCompanyQrVideosDialog = ({ companyId, open, onClose }: Props) => {
  const { _t } = useTranslationPrefix('CompanyQrsPage.videos');

  const { data, loading, refetch } = useQuery(CompanyQrVideosDocument, {
    variables: {
      input: {
        companyId: companyId,
      },
    },
  });
  const videos = data?.companyQrVideos;

  const [updateMutation] = useMutationWithSnackbar(
    'CompanyQrsPage.updateCompanyQrVideos',
    UpdateCompanyQrVideosDocument,
    {
      onCompleted: () => {
        refetch();
        onClose?.();
      },
    },
  );

  const handleSubmit = async (
    values: FormData,
    helpers: FormikHelpers<FormData>,
  ) => {
    await updateMutation({
      variables: {
        input: {
          companyId: companyId,
          ...values,
        },
      },
    });
  };

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={_t('title', 'Edit videos on QR page')}
      subtitle={_t('instruction', 'Enter IDs of videos from Vimeo')}
      disableEnforceFocus
    >
      {loading ? (
        <Loader />
      ) : (
        <Formik<FormData>
          initialValues={{
            primaryHeadsetWakeup: videos?.primaryHeadsetWakeup || '',
            backupHeadsetWakeup: videos?.backupHeadsetWakeup || '',
            launchInstruction: videos?.launchInstruction || '',
            primaryHeadsetInstruction: videos?.primaryHeadsetInstruction || '',
            backupHeadsetInstruction: videos?.backupHeadsetInstruction || '',
            returnHeadsetInstruction: videos?.returnHeadsetInstruction || '',
          }}
          onSubmit={handleSubmit}
          enableReinitialize
          validationSchema={getValidationSchema(_t)}
        >
          {({ isSubmitting }) => (
            <FormikForm>
              <Flex flexDirection="column" gap={3}>
                <FormikTextField
                  fullWidth
                  name="primaryHeadsetWakeup"
                  label={_t(
                    'primaryHeadsetWakeup',
                    'Primary (right) headset wakeup',
                  )}
                />
                <FormikTextField
                  fullWidth
                  name="backupHeadsetWakeup"
                  label={_t(
                    'backupHeadsetWakeup',
                    'Backup headset (left) wakeup',
                  )}
                />
                <FormikTextField
                  fullWidth
                  name="launchInstruction"
                  label={_t('launchInstruction', 'Launch instructions')}
                />
                <FormikTextField
                  fullWidth
                  name="primaryHeadsetInstruction"
                  label={_t(
                    'primaryHeadsetInstruction',
                    'Primary Headset Instruction',
                  )}
                />
                <FormikTextField
                  fullWidth
                  name="backupHeadsetInstruction"
                  label={_t(
                    'backupHeadsetInstruction',
                    'Backup Headset Instruction',
                  )}
                />
                <FormikTextField
                  fullWidth
                  name="returnHeadsetInstruction"
                  label={_t(
                    'returnHeadsetInstruction',
                    'Return Headset Instruction',
                  )}
                />

                <Flex
                  justifyContent="center"
                  mt={1}
                  gap={3}
                  flexDirection={{ xs: 'column-reverse', md: 'row' }}
                >
                  <Button
                    id="edit-qr-videos-cancel"
                    onClick={onClose}
                    color="primary"
                    variant="outlined"
                    disabled={isSubmitting}
                  >
                    {_t('cancel', 'Cancel')}
                  </Button>
                  <Button
                    id="edit-qr-videos-confirm"
                    type="submit"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                  >
                    {_t('submit', 'Save')}
                  </Button>
                </Flex>
              </Flex>
            </FormikForm>
          )}
        </Formik>
      )}
    </BaseDialog>
  );
};

export default EditCompanyQrVideosDialog;

const getValidationSchema = (_t: TFunctionPrefixed) =>
  Yup.object({
    primaryHeadsetWakeup: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
    backupHeadsetWakeup: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
    launchInstruction: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
    primaryHeadsetInstruction: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
    backupHeadsetInstruction: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
    returnHeadsetInstruction: Yup.number()
      .typeError(_t('idIsNumber', 'ID is numberic'))
      .integer(_t('idIsNumber', 'ID is numberic')),
  });
